'use strict';

require('../scss/chat.scss');

const SV = require('server-vars');
const get = require('lodash.get');
const root = typeof window === 'undefined' ? global : window;
const { localStorage, sessionStorage } = require('dibs-browser-storage');
const printRegex = /^\/print\//;
const isPrintPDP = root.location && printRegex.test(root.location.pathname);
const qs = require('querystring');
const { getBuyerId } = require('dibs-cookie-jar');
const { graphql } = require('react-relay');
const ScriptInjector = require('./scriptInjector');

const isPageType = require('./isPageType');
const { PAGE_TYPE, BROWSE_TYPES } = require('./pageConstants');
const { tradePromoDate } = (root.location && qs.parse(root.location.search)) || {};

const TYPE_SEARCH = 'SEARCH';
const TYPE_DEALER = 'SELLER';
const CHAT_STARTED_STORAGE_KEY = '__chatStarted';
const ARGUMENTS_STORAGE_KEY = '__chatArguments';
const CHECKOUT_UNLOAD_KEY = '__checkoutUnload';
const CHECKOUT_TYPE_KEY = '__checkoutTransactionType';
const CUSTOMER_TYPE_VIP = 'VIP';
const CUSTOMER_TYPE_TRADE = 'Trade';
const CUSTOMER_TYPE_CONSUMER = 'Consumer';

const ITEMS_ABOVE_PRICE_VIEWED_KEY = 'itemsAbovePriceViewed';
const ITEMS_ABOVE_PRICE_VIEWED_THRESHOLD = 3;

const PRICE_LIMIT_10K = 10000;
const PRICE_LIMIT_50K = 50000;

const TRADE_PROMO_DATE_FROM = tradePromoDate || '2017-01-16';

const intercomId = SV.get('intercomId');

const PAGE_TYPE_WHITELIST = [
    ...BROWSE_TYPES,
    PAGE_TYPE.FAVORITES, // Cart + Favorites has same pageType
    PAGE_TYPE.HOME,
    PAGE_TYPE.INVOICE,
    PAGE_TYPE.PDP,
    PAGE_TYPE.SEARCH,
    PAGE_TYPE.SOURCING,
];

let loadPromise = null;

function loadIntercom() {
    loadPromise = ScriptInjector.inject(`https://widget.intercom.io/widget/${intercomId}`).then(
        () => {
            const intercom = root.Intercom;
            intercom('boot', {
                app_id: intercomId, // eslint-disable-line camelcase
            });

            return intercom;
        }
    );
    return loadPromise;
}

function getIntercom() {
    if (root.intercom || loadPromise) {
        return loadPromise;
    }
    return loadIntercom();
}

function getDate() {
    const date = new Date();
    return date.toJSON();
}

function getIntercomDate(date) {
    date = new Date(date);
    return date.getTime() / 1000; // get UTC seconds
}

function applyArgumentQueue(intercom) {
    const argumentQueue = sessionStorage.getItem(ARGUMENTS_STORAGE_KEY) || [];

    if (argumentQueue.length) {
        argumentQueue.forEach(args => {
            intercom.apply(intercom, args);
        });
    }

    sessionStorage.setItem(ARGUMENTS_STORAGE_KEY, []);
}

function load(...args) {
    const chatStarted = sessionStorage.getItem(CHAT_STARTED_STORAGE_KEY);
    const pageTypeValid = isPageType(PAGE_TYPE_WHITELIST);

    if (chatStarted && !isPrintPDP && pageTypeValid) {
        return getIntercom().then(intercom => {
            applyArgumentQueue(intercom);
            if (args.length) {
                intercom.apply(intercom, args);
            }
        });
    } else if (!isPrintPDP) {
        const argumentQueue = sessionStorage.getItem(ARGUMENTS_STORAGE_KEY) || [];
        argumentQueue.push(args);
        sessionStorage.setItem(ARGUMENTS_STORAGE_KEY, argumentQueue);
    }

    // if `chat_intercom` is not enabled resolve
    return Promise.resolve();
}

function trackEvent(...args) {
    return load('trackEvent', ...args);
}

function update(data) {
    return load('update', data);
}

function getUserTestGroup(tradeUserCreateDate) {
    const userId = getBuyerId(root.document.cookie);
    const lastIdNumber = Number.parseInt(userId.slice(-1));
    const testGroupData = {};

    // User ids end up with 1 or 2, ~20% of users
    if ([1, 2].indexOf(lastIdNumber) > -1) {
        testGroupData.user_test_group = 'Holdout'; // eslint-disable-line camelcase
    }

    testGroupData.user_test_group = 'Test'; // eslint-disable-line camelcase

    if (tradeUserCreateDate) {
        const secontToLastIdNumber = Number.parseInt(userId.slice(-2, -1));
        if (
            [4, 5].indexOf(secontToLastIdNumber) > -1 &&
            new Date(tradeUserCreateDate) > new Date(TRADE_PROMO_DATE_FROM)
        ) {
            testGroupData.user_trade_promo_group = 'Test'; // eslint-disable-line camelcase
        } else {
            testGroupData.user_trade_promo_group = 'Control'; // eslint-disable-line camelcase
        }
    }

    return testGroupData;
}

function getCustomerType(user) {
    const customerType = [];

    if (user && user.isTrade) {
        customerType.push(CUSTOMER_TYPE_TRADE);
    }

    if (user && user.hasVipPermission) {
        customerType.push(CUSTOMER_TYPE_VIP);
    }

    if (!customerType.length) {
        return CUSTOMER_TYPE_CONSUMER;
    }

    return customerType.length ? customerType.join(' and ') : undefined;
}

const BUYER_STATUS_CONFIRMED_MULTIPLE = 'CONFIRMED_MULTIPLE';
const BUYER_STATUS_CONFIRMED_ONE = 'CONFIRMED_ONE';
const BUYER_STATUS_SUBMITTED = 'SUBMITTED';
const BUYER_STATUS_INITIATED = 'INITIATED';
const BUYER_STATUS_NONE = 'NONE';

const TRADE_STATUS_MAP = {
    VERIFIED_VETTED: 'verified - vetted',
    VERIFIED_PRE_APPROVED: 'verified - pre-approved',
    SUSPECTED: 'suspected',
    APPLIED_IN_REVIEW: 'applied - in review',
    APPLIED_NOT_ENOUGH_INFORMATION: 'applied - not enough information',
    APPLIED_REJECTED: 'applied - rejected',
};

function getBuyerStatusFromTransactions(viewer) {
    const { initiatedTransactions, submittedTransactions, confirmedTransactions } = viewer;

    if (confirmedTransactions && confirmedTransactions.totalResults > 1) {
        return BUYER_STATUS_CONFIRMED_MULTIPLE;
    } else if (confirmedTransactions && confirmedTransactions.totalResults) {
        return BUYER_STATUS_CONFIRMED_ONE;
    } else if (submittedTransactions && submittedTransactions.totalResults) {
        return BUYER_STATUS_SUBMITTED;
    } else if (initiatedTransactions && initiatedTransactions.totalResults) {
        return BUYER_STATUS_INITIATED;
    }

    return BUYER_STATUS_NONE;
}

function getTradeStatus(viewer) {
    const roles = get(viewer, 'user.roles.roleList') || [];
    const role = roles.find(r => r.name === 'B_TRADE_BUYER');
    const statusKey = get(role, 'userData.userStatus.name');
    return {
        status: TRADE_STATUS_MAP[statusKey],
        date: get(role, 'createdDate'),
    };
}

const chat = {
    start() {
        sessionStorage.setItem(CHAT_STARTED_STORAGE_KEY, true);
        return load();
    },

    show() {
        return load('show');
    },

    fillUserData(viewer, user) {
        const { tradeChatEmail, vipChatEmail } = viewer;
        const { tradeUserCreateDate } = user;
        const userId = getBuyerId(root.document.cookie);
        if (!userId) {
            return Promise.resolve();
        }

        const userType = getCustomerType(user);
        const { createdDate, profile: { firstName, lastName, email } } = user;
        const tradeStatus = getTradeStatus(viewer);

        const userData = Object.assign(
            {
                user_id: userId, // eslint-disable-line camelcase
                name: `${firstName || ''} ${lastName || ''}`.trim(),
                email: email,
                user_type: userType, // eslint-disable-line camelcase
                registered_at: getIntercomDate(createdDate), // eslint-disable-line camelcase
                trade_account_manager: get(tradeChatEmail, '0.userDataB.0.email'), // eslint-disable-line camelcase
                vip_account_manager: get(vipChatEmail, '0.userDataB.0.email'), // eslint-disable-line camelcase
                buyer_status: getBuyerStatusFromTransactions(viewer), // eslint-disable-line camelcase
                total_transactions: get(viewer, 'allTransactions.totalResults') || 0, // eslint-disable-line camelcase
                trade_status: tradeStatus.status, // eslint-disable-line camelcase
                verified_trade_at: getIntercomDate(tradeStatus.date), // eslint-disable-line camelcase
            },
            getUserTestGroup(tradeUserCreateDate)
        );

        return update(userData);
    },

    customAttribute: {
        itemsInCart(itemCount) {
            return update({ items_in_cart: itemCount }); // eslint-disable-line camelcase
        },

        // TODO: Remove, as never used?
        registeredOnPage() {
            const { pathname } = root.location;
            return update({ registered_on_page: pathname }); // eslint-disable-line camelcase
        },

        // TODO: Remove, as never used?
        loyaltyStatus(status) {
            return update({ LoyaltyStatus: status });
        },
    },

    track: {
        abandonFavorite(type) {
            switch (type) {
                case TYPE_SEARCH:
                    return trackEvent('abandon_follow_search');

                case TYPE_DEALER:
                    return trackEvent('abandon_follow_dealer');
            }

            return Promise.resolve();
        },

        abandonRequestNetPrice() {
            return trackEvent('abandon_request_net_price', {
                fired_at: getDate(), // eslint-disable-line camelcase
            });
        },

        requestNetPrice() {
            return trackEvent('success_request_net_price', {
                fired_at: getDate(), // eslint-disable-line camelcase
            });
        },

        abandonMakeOffer() {
            return trackEvent('make_offer_abandon', {
                fired_at: getDate(), // eslint-disable-line camelcase
            });
        },

        makeOfferComplete() {
            return trackEvent('make_offer_complete', {
                fired_at: getDate(), // eslint-disable-line camelcase
            });
        },

        abandonContactDealer() {
            return trackEvent('abandon_contact_dealer', {
                fired_at: getDate(), // eslint-disable-line camelcase
            });
        },

        contactDealer() {
            return trackEvent('contact_dealer', {
                fired_at: getDate(), // eslint-disable-line camelcase
            });
        },

        addToCart() {
            return trackEvent('item_added_to_cart', {
                fired_at: getDate(), // eslint-disable-line camelcase
            });
        },

        itemSaved() {
            return trackEvent('success_save_item');
        },

        shippingDetailsClick() {
            return trackEvent('view_shipping_details');
        },

        buyerAssuranceClick() {
            return trackEvent('buyer_protection');
        },

        checkoutReached(data, isNegotiable) {
            sessionStorage.setItem(CHECKOUT_TYPE_KEY, { isNegotiable });
            return trackEvent('checkout_reached', data);
        },

        checkoutComplete() {
            return trackEvent('checkout_completed');
        },

        checkoutAbandon() {
            return trackEvent('checkout_abandon');
        },

        checkoutSurveySubmitted(data) {
            return trackEvent('checkout_survey_submitted', data);
        },

        tradeBasicInfoCompleted(noWebsite) {
            const prefix = noWebsite ? 'no ' : '';
            return trackEvent(`trade application step 1 - ${prefix}website`);
        },

        sourcingRequestSubmitted() {
            return trackEvent('SourcingRequest');
        },
    },
};

function checkAbandonCheckout() {
    if (!root.location) {
        return;
    }
    const { pathname } = root.location;
    const isCheckoutPage = pathname.indexOf('checkout') > -1;
    const isCheckoutConfirmationPage = pathname.indexOf('checkout/confirmation') > -1;
    const isMyOrdersPage = pathname.indexOf('my/orders') > -1;

    if (isCheckoutPage && !isCheckoutConfirmationPage) {
        root.onbeforeunload = () => {
            sessionStorage.setItem(CHECKOUT_UNLOAD_KEY, true);
        };
    } else if (
        !isCheckoutConfirmationPage &&
        !isMyOrdersPage &&
        sessionStorage.getItem(CHECKOUT_UNLOAD_KEY)
    ) {
        const isNegotiable = get(sessionStorage.getItem(CHECKOUT_TYPE_KEY), 'isNegotiable');

        if (isNegotiable) {
            chat.track.abandonMakeOffer();
        } else {
            chat.track.checkoutAbandon();
        }
    }

    sessionStorage.deleteItem(CHECKOUT_UNLOAD_KEY);
}

function checkConductSearch() {
    if (!root.location) {
        return;
    }
    const { pathname, search } = root.location;

    if (pathname.indexOf('/search') === 0 && search.indexOf('?q=') === 0) {
        update({ user_conduct_search: 'Yes' }); // eslint-disable-line camelcase
    }
}

function checkItemsAbovePrice(key, eventName) {
    const items = localStorage.getItem(ITEMS_ABOVE_PRICE_VIEWED_KEY) || {};
    let newValue = (items[key] || 0) + 1;

    if (newValue >= ITEMS_ABOVE_PRICE_VIEWED_THRESHOLD) {
        trackEvent(eventName);
        newValue = 0;
    }
    localStorage.setItem(ITEMS_ABOVE_PRICE_VIEWED_KEY, {
        ...items,
        [key]: newValue,
    });
}

function checkPdp() {
    if (isPageType([PAGE_TYPE.PDP])) {
        const pk = SV.get('item.pk');
        trackEvent('pdp_view', { pk });

        const isUserTypeTrade = SV.get('settings.isUserTypeTrade');

        if (isUserTypeTrade) {
            const { netPrice, price } = SV.get('data') || {};
            const tradePrice = netPrice || price;
            if (tradePrice > PRICE_LIMIT_50K) {
                checkItemsAbovePrice(PRICE_LIMIT_50K, 'ItemView50k');
            } else if (tradePrice > PRICE_LIMIT_10K) {
                checkItemsAbovePrice(PRICE_LIMIT_10K, 'ItemView10k');
            }
        }
    }
}

checkAbandonCheckout();
checkConductSearch();
checkPdp();

export const userFragment = graphql`
    fragment chat_user on User {
        createdDate
        isTrade: hasPermission(name: "B_TRADE_CHECKOUT")
        hasVipPermission: hasPermission(name: "B_VIP_CHECKOUT")
        profile {
            email
        }
        roles {
            roleList {
                name
                createdDate
                userData {
                    userStatus {
                        name
                    }
                }
            }
        }
    }
`;

export const viewerFrgament = graphql`
    fragment chat_viewer on Viewer {
        tradeChatEmail: userRelationships(userIds: $userIds, relationshipType: VIP) {
            userDataB {
                ... on VipRepData {
                    email
                }
            }
        }
        vipChatEmail: userRelationships(userIds: $userIds, relationshipType: TRADE) {
            userDataB {
                ... on TradeRepData {
                    email
                }
            }
        }
        allTransactions: transactions(buyerId: $userIds, first: 0) {
            totalResults
        }
        initiatedTransactions: transactions(
            buyerId: $userIds
            viewName: "buyerTracking.initiated"
            first: 0
        ) {
            totalResults
        }
        submittedTransactions: transactions(
            buyerId: $userIds
            viewName: "buyerTracking.submitted"
            first: 0
        ) {
            totalResults
        }
        confirmedTransactions: transactions(
            buyerId: $userIds
            sort: "createdDate asc"
            viewName: "buyerTracking.confirmed"
            first: 0
        ) {
            totalResults
        }
    }
`;

export default chat;



// WEBPACK FOOTER //
// ./src/utils/chat.js